import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../hooks/useCart";
import { useFavorites } from "../hooks/useFavorites";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSnowflake,
  faTemperatureArrowDown,
  faJar,
  faImages,
  faList,
  faLightbulb,
  faHeart as faHeartSolid,
} from "@fortawesome/free-solid-svg-icons";
import { faHeart as faHeartRegular } from "@fortawesome/free-regular-svg-icons";
import newItem from "../components/images/new-item.png";
import FsLightbox from "fslightbox-react";

//styles
import "./Products.css";

const ProductCard = ({ item }) => {
  const navigate = useNavigate();
  const { cart, updateCart } = useCart();
  const { isFavorite, toggleFavorite } = useFavorites();

  // Helper function to get date abbreviation description
  const getDateDescription = (dateCode) => {
    const descriptions = {
      BB: "BB = Best By Date",
      UB: "UB = Use By Date",
      EX: "EX = Expiration Date",
      FB: "FB = Freeze By Date",
      PD: "PD = Production Date",
      SB: "SB = Sell By Date",
      CS: "CS = Date on Case Only",
      JD: "JD = Julian Date",
    };
    return descriptions[dateCode] || "";
  };

  // Helper function to get the most recent price drop
  const getPriceDrop = () => {
    if (!item.updateLog || item.updateLog.length === 0) return null;

    // Sort by dateUpdated descending to get most recent
    const sortedLog = [...item.updateLog].sort(
      (a, b) => new Date(b.dateUpdated) - new Date(a.dateUpdated)
    );

    const mostRecentUpdate = sortedLog[0];
    const previousPrice = mostRecentUpdate.casePrice;
    const currentPrice = item.casePrice;

    // Only show if price has dropped
    if (currentPrice < previousPrice) {
      return {
        current: currentPrice,
        previous: previousPrice,
      };
    }

    return null;
  };

  const priceDrop = getPriceDrop();
  const quantity = cart
    ? cart.find((x) => x.productId === item.productId)?.quantity ?? null
    : null;
  const [showTemp, setShowTemp] = useState(false);
  const [lightboxToggler, setLightboxToggler] = useState(0);
  const [showDateInfo, setShowDateInfo] = useState(false);

  // Auto-hide date info after 3 seconds
  useEffect(() => {
    if (showDateInfo) {
      const timer = setTimeout(() => {
        setShowDateInfo(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showDateInfo]);

  const handleShowTemp = () => {
    showTemp ? setShowTemp(false) : setShowTemp(true);
  };

  const handleShowImage = () => {
    setLightboxToggler((prev) => prev + 1);
  };

  const handleImageType = (image) => {
    return typeof image === "string" ? [image] : image;
  };

  const navigateToProduct = () => {
    // Create URL-friendly slug from description
    const slug = item.description
      .replace(/[^a-zA-Z0-9\s]/g, "") // Remove special characters
      .trim()
      .replace(/\s+/g, "-"); // Replace spaces with hyphens
    navigate(`/${slug}/${item.productId}`);
  };

  return (
    <div className="cell">
      <FsLightbox
        toggler={lightboxToggler}
        sources={handleImageType(item.image)}
        types={handleImageType(item.image).map(() => "image")}
      />
      <div className="item-grid">
        <div className="grid-child-1">
          {!item.numberOfCustomers &&
            new Date(item.dateAdded).getTime() >
              new Date().getTime() - 259200 * 1000 && (
              <img src={newItem} alt="" className="newItem" />
            )}
          <img
            src={item.thumbnailUrl || item.thumbnail}
            className="item-photo"
            alt={item.description}
            onClick={navigateToProduct}
            style={{ cursor: "pointer" }}
          />
          <div className="product-icons-container">
            {item.hasIngredients && (
              <FontAwesomeIcon
                icon={faList}
                className="faIngredients"
                onClick={navigateToProduct}
                style={{ cursor: "pointer" }}
                title="View Ingredients"
              />
            )}
            {item.imageCount > 1 && (
              <FontAwesomeIcon
                icon={faImages}
                className="faImages"
                onClick={navigateToProduct}
                style={{ cursor: "pointer" }}
                title="View additional images"
              />
            )}
          </div>
        </div>
        <div className="grid-child-2">
          <div className="price">
            {priceDrop ? (
              <>
                <div className="price-drop-container">
                  <p className="product-price price-drop-now">
                    Now ${Number.parseFloat(priceDrop.current / 100).toFixed(2)}
                  </p>
                  <p className="price-drop-was">
                    Was $
                    {Number.parseFloat(priceDrop.previous / 100).toFixed(2)}
                  </p>
                </div>
                <p className="price-per-unit">
                  (${Number.parseFloat(item.unitPrice / 100).toFixed(2)} /{" "}
                  {item.unitLabel === "lb." ? "lb." : "item"}
                  {item.pricePerUnit &&
                    item.unitLabel !== "lb." &&
                    ` | ${item.pricePerUnit}`}
                  )
                </p>
              </>
            ) : (
              <>
                <p className="product-price">{`${item.unitCount} ${
                  item.unitLabel === "lb." ? "lb." : ""
                } for $${Number.parseFloat(item.casePrice / 100).toFixed(
                  2
                )}`}</p>
                <p className="price-per-unit">
                  (${Number.parseFloat(item.unitPrice / 100).toFixed(2)} /{" "}
                  {item.unitLabel === "lb." ? "lb." : "item"}
                  {item.pricePerUnit &&
                    item.unitLabel !== "lb." &&
                    ` | ${item.pricePerUnit}`}
                  )
                </p>
              </>
            )}
          </div>
          <p
            className="product-description"
            onClick={navigateToProduct}
            style={{ cursor: "pointer" }}
          >
            {item.description}, {item.unitSize}
          </p>
          <div className="cell-container">
            {item.date.slice(0, 2) === "BB" && (
              <p className="show">
                <b>BB</b> - Best By Date
              </p>
            )}
            {item.date.slice(0, 2) === "UB" && (
              <p className="show">
                <b>UB</b> - Use By Date
              </p>
            )}
            {item.date.slice(0, 2) === "EX" && (
              <p className="show">
                <b>EX</b> - Expiration Date
              </p>
            )}
            {item.date.slice(0, 2) === "FB" && (
              <p className="show">
                <b>FB</b> - Freeze By Date
              </p>
            )}
            {item.date.slice(0, 2) === "PD" && (
              <p className="show">
                <b>PD</b> - Production Date
              </p>
            )}
            {item.date.slice(0, 2) === "SB" && (
              <p className="show">
                <b>SB</b> - Sell By Date
              </p>
            )}
            {item.date.slice(0, 2) === "CS" && (
              <p className="show">
                <b>CS</b> - Date on Case Only
              </p>
            )}
            {item.date.slice(0, 2) === "JD" && (
              <p className="show">
                <b>JD</b> - Julian Date
              </p>
            )}
            {item.date.trim().length > 3 && (
              <p className="cell-item product-date">
                {item.date}
                <FontAwesomeIcon
                  icon={faLightbulb}
                  style={{
                    marginLeft: "6px",
                    fontSize: "0.85em",
                    color: "rgba(150, 150, 150, 0.3)",
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDateInfo(!showDateInfo);
                  }}
                />
                {showDateInfo && (
                  <span
                    style={{
                      display: "block",
                      fontSize: "0.85em",
                      color: "#666",
                      fontStyle: "italic",
                      marginTop: "4px",
                    }}
                  >
                    {getDateDescription(item.date.slice(0, 2))}
                  </span>
                )}
              </p>
            )}
            <p className="cell-item" style={{ marginLeft: "auto" }}>
              Cases Available: <b>{item.quantityAvailable}</b>
            </p>
            {item.quantityAvailable === 0 && <p>Out of Stock</p>}
          </div>
          <div className="add-action-row">
            {item.casePrice && (
              <div className="addButton">
                {!quantity && item.quantityAvailable !== 0 && (
                  <button
                    type="button"
                    className="button"
                    onClick={() => {
                      updateCart("ADD_ITEM", item);
                    }}
                  >
                    + Add
                  </button>
                )}
                {item.quantityAvailable === 0 && item.numberOfCustomers && (
                  <h3>Sold out :)</h3>
                )}
                {item.quantityAvailable === 0 && !item.numberOfCustomers && (
                  <button
                    type="button"
                    className="button"
                    onClick={() => {
                      updateCart("REMOVE_ITEM", item);
                    }}
                  >
                    Remove
                  </button>
                )}
                {quantity && item.quantityAvailable > 0 && (
                  <div className="addition">
                    <button
                      onClick={() => {
                        updateCart("REMOVE_ITEM", item);
                      }}
                    >
                      -
                    </button>
                    <span> {quantity > 0 && quantity} </span>
                    <button
                      onClick={() => {
                        updateCart("ADD_ITEM", item);
                      }}
                      disabled={quantity >= item.quantityAvailable}
                    >
                      +
                    </button>
                  </div>
                )}
              </div>
            )}
            <FontAwesomeIcon
              icon={isFavorite(item.productId) ? faHeartSolid : faHeartRegular}
              className={`favorite-heart ${
                isFavorite(item.productId) ? "favorited" : ""
              }`}
              onClick={(e) => {
                e.stopPropagation();
                toggleFavorite(item.productId);
              }}
            />
          </div>
          <div className="white-circle">
            {item.temperature[0] === "F" && !showTemp && (
              <FontAwesomeIcon
                icon={faSnowflake}
                className="temperature-icon"
                onClick={() => handleShowTemp()}
              />
            )}
            {item.temperature[0] === "F" && showTemp && (
              <p className="icon-description" onClick={() => handleShowTemp()}>
                Frozen
              </p>
            )}
            {item.temperature[0] === "R" && !showTemp && (
              <FontAwesomeIcon
                icon={faTemperatureArrowDown}
                className="temperature-icon"
                onClick={() => handleShowTemp()}
              />
            )}
            {item.temperature[0] === "R" && showTemp && (
              <p className="icon-description" onClick={() => handleShowTemp()}>
                Refrigerated
              </p>
            )}
            {item.temperature[0] === "D" && !showTemp && (
              <FontAwesomeIcon
                icon={faJar}
                className="temperature-icon"
                onClick={() => handleShowTemp()}
              />
            )}
            {item.temperature[0] === "D" && showTemp && (
              <p className="icon-description" onClick={() => handleShowTemp()}>
                Non-Perishable
              </p>
            )}
          </div>
        </div>
      </div>
      <p className="cell-product-id">
        <b>#{item.productId}</b>
      </p>
      {item.numberOfCustomers && item.numberOfCustomers >= 1 && (
        <p className="popular-item">
          {item.numberOfCustomers}{" "}
          {item.numberOfCustomers > 1 ? "cases" : "case"} purchased this month!
        </p>
      )}
      {item.numberOfCustomers && item.numberOfCustomers >= 1 && (
        <p className="popular-item-number">{item.numberOfCustomers}</p>
      )}
    </div>
  );
};

export default ProductCard;
